import { chatDrawerActions, chatDrawerSelectors, ChatSuggestionsPreferences } from 'ducks/chatDrawer'
import { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from 'types/state'

const STORAGE_KEY = 'ada_chat_suggestions_preferences'
const DEFAULT_PREFERENCES: ChatSuggestionsPreferences = {
  showSuggestionsOnEmptyChat: true,
  showCompactSuggestions: false,
}

export const useChatSuggestionsPreferences = () => {
  const dispatch = useDispatch()
  const preferences = useSelector((state: RootState) => chatDrawerSelectors.getChatSuggestionsPreferences(state))

  // Sync with localStorage changes from other tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && e.newValue) {
        try {
          const parsed = JSON.parse(e.newValue) as Partial<ChatSuggestionsPreferences>
          const updated = { ...DEFAULT_PREFERENCES, ...parsed }
          dispatch(chatDrawerActions.setChatSuggestionsPreferences(updated))
        } catch (error) {
          console.error('Error parsing chat suggestions preferences from storage event:', error)
        }
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => {
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [dispatch])

  const updatePreferences = useCallback(
    (updates: Partial<ChatSuggestionsPreferences>) => {
      dispatch(chatDrawerActions.setChatSuggestionsPreferences(updates))
    },
    [dispatch]
  )

  const setShowSuggestionsOnEmptyChat = useCallback(
    (value: boolean) => {
      updatePreferences({ showSuggestionsOnEmptyChat: value })
    },
    [updatePreferences]
  )

  const setShowCompactSuggestions = useCallback(
    (value: boolean) => {
      updatePreferences({ showCompactSuggestions: value })
    },
    [updatePreferences]
  )

  return {
    preferences,
    setShowSuggestionsOnEmptyChat,
    setShowCompactSuggestions,
    updatePreferences,
  }
}
